import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, User, Check, X, AlertCircle, ArrowRight, UserCheck, Truck } from 'lucide-react';
import { apiGet, apiPatch } from '../shared/apiClient';
import type { Squadra } from '../shared/types';
import { getTurnoDateStrRome } from '../shared/tz';
import { useToast } from '../hooks/useToast';

export interface DotazioneConfermataData {
  collaboratori: string | null;
  mezzoTarga: string | null;
  autista: string | null;
}

export interface ModalConfermaSquadraTurnoProps {
  isOpen: boolean;
  onClose: () => void;
  squadraMe?: Squadra | null;
  onConfermato?: (data: DotazioneConfermataData) => void;
  motivo?: 'inizio_report' | 'modifica_manuale' | 'dashboard';
}

export function ModalConfermaSquadraTurno({
  isOpen,
  onClose,
  squadraMe,
  onConfermato,
  motivo = 'inizio_report',
}: ModalConfermaSquadraTurnoProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const turnoOggi = useMemo(() => getTurnoDateStrRome(), []);
  const myTeamName = squadraMe?.nome || 'La tua squadra';

  // Query altre squadre attive
  const { data: altreSquadreData } = useQuery<{ id: string; nome: string }[]>({
    queryKey: ['squadre-altre'],
    queryFn: async () => {
      const { data } = await apiGet<{ id: string; nome: string }[]>('/squadre/altre');
      return data;
    },
    enabled: isOpen,
  });
  const altreSquadre = useMemo(() => altreSquadreData || [], [altreSquadreData]);

  // Stati form dotazione
  const [targaInput, setTargaInput] = useState('');
  const [selectedSquadre, setSelectedSquadre] = useState<string[]>([]);
  const [hasAltro, setHasAltro] = useState(false);
  const [altroInput, setAltroInput] = useState('');
  const [selectedAutista, setSelectedAutista] = useState('');

  // Inizializza la selezione quando si apre la modale
  useEffect(() => {
    if (isOpen && squadraMe) {
      setTargaInput(squadraMe.mezzoTarga || '');
      setSelectedAutista(squadraMe.autista || myTeamName);

      if (squadraMe.collaboratori) {
        const parts = squadraMe.collaboratori.split(',').map((s) => s.trim()).filter(Boolean);
        const otherNames = (altreSquadreData || []).map((s) => s.nome);
        const matched: string[] = [];
        const nonMatched: string[] = [];
        for (const p of parts) {
          if (otherNames.includes(p)) {
            matched.push(p);
          } else {
            nonMatched.push(p);
          }
        }
        setSelectedSquadre(matched);
        if (nonMatched.length > 0) {
          setHasAltro(true);
          setAltroInput(nonMatched.join(', '));
        } else {
          setHasAltro(false);
          setAltroInput('');
        }
      } else {
        setSelectedSquadre([]);
        setHasAltro(false);
        setAltroInput('');
      }
    }
  }, [isOpen, squadraMe, altreSquadreData, myTeamName]);

  // Lock scroll background quando la modale è aperta
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Lista dinamica collaboratori per determinare chi può guidare
  const collabList = [
    ...selectedSquadre,
    ...(hasAltro && altroInput.trim() ? [altroInput.trim()] : []),
  ];
  const autistiDisponibili = Array.from(new Set([myTeamName, ...collabList]));
  const effectiveAutista = (selectedAutista && autistiDisponibili.includes(selectedAutista))
    ? selectedAutista
    : myTeamName;

  // Mutation salvataggio dotazione / turno
  const dotazioneMutation = useMutation({
    mutationFn: async (payload: {
      mezzoTarga: string | null;
      collaboratori: string | null;
      autista: string | null;
      dotazioneTurno: string;
    }) => {
      const { data } = await apiPatch<Squadra>('/squadre/me/dotazione', payload);
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['squadra-me'], updated);
      queryClient.invalidateQueries({ queryKey: ['squadra-me'] });
      toast({ type: 'success', message: 'Dotazione e squadra confermate per il turno' });
      onConfermato?.({
        collaboratori: updated.collaboratori || null,
        mezzoTarga: updated.mezzoTarga || null,
        autista: updated.autista || null,
      });
      onClose();
    },
    onError: () => {
      toast({ type: 'error', message: 'Errore nel salvataggio della dotazione squadra' });
    },
  });

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const vecchioCollab = squadraMe?.collaboratori?.trim() || null;
  const isPending = dotazioneMutation.isPending;

  // 1-Tap: Conferma vecchio collaboratore (mantiene targa e autista)
  const handleConfermaVecchio = () => {
    if (!vecchioCollab) return;
    dotazioneMutation.mutate({
      mezzoTarga: targaInput.trim() || null,
      collaboratori: vecchioCollab,
      autista: effectiveAutista || myTeamName,
      dotazioneTurno: turnoOggi,
    });
  };

  // 1-Tap: Lavoro da solo (guida automatica: tu)
  const handleLavoroDaSolo = () => {
    dotazioneMutation.mutate({
      mezzoTarga: targaInput.trim() || null,
      collaboratori: null,
      autista: myTeamName,
      dotazioneTurno: turnoOggi,
    });
  };

  // Salva selezione personalizzata completa
  const handleSalvaPersonalizzato = () => {
    const collabStr = collabList.join(', ').trim() || null;
    dotazioneMutation.mutate({
      mezzoTarga: targaInput.trim() || null,
      collaboratori: collabStr,
      autista: effectiveAutista.trim() || myTeamName,
      dotazioneTurno: turnoOggi,
    });
  };

  const toggleSquadra = (nome: string) => {
    const next = selectedSquadre.includes(nome)
      ? selectedSquadre.filter((n) => n !== nome)
      : [...selectedSquadre, nome];
    setSelectedSquadre(next);
    const nextCollabs = [...next, ...(hasAltro && altroInput.trim() ? [altroInput.trim()] : [])];
    if (selectedAutista !== myTeamName && !nextCollabs.includes(selectedAutista)) {
      setSelectedAutista(myTeamName);
    }
  };

  const toggleAltro = () => {
    const nextHasAltro = !hasAltro;
    setHasAltro(nextHasAltro);
    const nextCollabs = [...selectedSquadre, ...(nextHasAltro && altroInput.trim() ? [altroInput.trim()] : [])];
    if (selectedAutista !== myTeamName && !nextCollabs.includes(selectedAutista)) {
      setSelectedAutista(myTeamName);
    }
  };

  const stringaSelezionata = collabList.join(', ');

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in safe-bottom">
      <div className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[88vh] overflow-hidden animate-slide-up border border-slate-200">
        {/* Header Modale */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-gradient-to-r from-blue-50 via-slate-50 to-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold shadow-xs">
              <Users className="w-5 h-5 text-blue-100" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="text-base sm:text-lg font-black text-slate-900 leading-tight">
                  Dotazione e Squadra del Turno
                </h3>
                <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-800">
                  Oggi
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium">
                {motivo === 'inizio_report'
                  ? 'Verifica targa, colleghi e guida prima di compilare il report'
                  : 'Aggiorna targa mezzo, collaboratori e guida per questo turno'}
              </p>
            </div>
          </div>
          {motivo === 'modifica_manuale' && (
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Corpo Modale Scrollabile */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-4">
          {/* Sezione 1: Targa Mezzo */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3 space-y-1.5">
            <label className="block text-[11px] font-bold text-slate-700 flex items-center gap-1.5 uppercase tracking-wider">
              <Truck className="w-3.5 h-3.5 text-blue-600" />
              <span>Targa del mezzo in dotazione:</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={targaInput}
                disabled={isPending}
                onChange={(e) => setTargaInput(e.target.value.toUpperCase())}
                placeholder="es. AB 123 CD"
                className="w-full px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-800 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-hidden transition"
              />
            </div>
          </div>

          {/* Sezione 2: Azioni Rapide a 1 Tap per Collaboratori */}
          <div className="space-y-2">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Chi collabora con te oggi?
            </label>

            {/* Opzione 1: Conferma vecchio collaboratore */}
            {vecchioCollab && (
              <button
                type="button"
                disabled={isPending}
                onClick={handleConfermaVecchio}
                className="w-full text-left p-3 rounded-2xl border-2 border-blue-500/80 bg-blue-50/50 hover:bg-blue-100/60 active:scale-[0.99] transition flex items-center justify-between gap-3 shadow-xs cursor-pointer"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
                    <UserCheck className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-black text-blue-900 truncate">
                      Conferma ancora con: <span className="underline decoration-blue-400">{vecchioCollab}</span>
                    </div>
                    <div className="text-[11px] text-blue-700 font-medium truncate">
                      1 tap: conferma la stessa coppia e procede
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-600 shrink-0" />
              </button>
            )}

            {/* Opzione 2: Lavoro da solo */}
            <button
              type="button"
              disabled={isPending}
              onClick={handleLavoroDaSolo}
              className={`w-full text-left p-3 rounded-2xl border transition flex items-center justify-between gap-3 ${
                !vecchioCollab
                  ? 'border-2 border-emerald-500/80 bg-emerald-50/50 hover:bg-emerald-100/60'
                  : 'border-slate-200 bg-white hover:bg-slate-50'
              } active:scale-[0.99] cursor-pointer`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                  !vecchioCollab ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  <User className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-black text-slate-800 truncate">
                    Oggi lavoro da solo
                  </div>
                  <div className="text-[11px] text-slate-500 font-medium truncate">
                    Nessun secondo operatore per questo turno
                  </div>
                </div>
              </div>
              <Check className={`w-4 h-4 ${!vecchioCollab ? 'text-emerald-600' : 'text-slate-400'} shrink-0`} />
            </button>
          </div>

          {/* Divisore selezione altri colleghi */}
          <div className="relative pt-1 pb-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-wider text-slate-400">
              <span className="bg-white px-2">oppure scegli / indica altro collega</span>
            </div>
          </div>

          {/* Chip selezione altre squadre / Altro */}
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5 items-center">
              {altreSquadre.map((sq) => {
                const isSelected = selectedSquadre.includes(sq.nome);
                return (
                  <button
                    key={sq.id}
                    type="button"
                    disabled={isPending}
                    onClick={() => toggleSquadra(sq.nome)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border active:scale-95 cursor-pointer ${
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                        : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    <span>{sq.nome}</span>
                    {isSelected && <span className="text-[10px]">✓</span>}
                  </button>
                );
              })}

              <button
                type="button"
                disabled={isPending}
                onClick={toggleAltro}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border active:scale-95 cursor-pointer ${
                  hasAltro
                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                    : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                }`}
              >
                <span>Altro nome...</span>
                {hasAltro && <span className="text-[10px]">✓</span>}
              </button>
            </div>

            {/* Input nome libero */}
            {hasAltro && (
              <div className="pt-1 animate-fade-in">
                <input
                  type="text"
                  value={altroInput}
                  disabled={isPending}
                  onChange={(e) => setAltroInput(e.target.value)}
                  placeholder="Nome e cognome collega o ditta..."
                  className="w-full px-3 py-2 text-xs font-semibold text-slate-800 bg-white border border-blue-400 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Sezione 3: Chi Guida il Mezzo (Autista) */}
          <div className="bg-amber-50/60 border border-amber-200/80 rounded-2xl p-3 space-y-2">
            <label className="block text-[11px] font-bold text-amber-900 flex items-center gap-1.5 uppercase tracking-wider">
              <User className="w-3.5 h-3.5 text-amber-600" />
              <span>Chi guida il mezzo durante il turno?</span>
            </label>
            <div className="flex flex-wrap gap-1.5 items-center">
              {autistiDisponibili.map((nome) => {
                const isSelected = effectiveAutista === nome;
                return (
                  <button
                    key={nome}
                    type="button"
                    disabled={isPending}
                    onClick={() => setSelectedAutista(nome)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border active:scale-95 cursor-pointer ${
                      isSelected
                        ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-amber-100/50'
                    }`}
                  >
                    <span>{nome === myTeamName ? `${nome} (Tu)` : nome}</span>
                    {isSelected && <span className="text-[10px]">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Anteprima selezione se modificata */}
          {(stringaSelezionata || targaInput || effectiveAutista) && (
            <div className="p-2.5 rounded-xl bg-blue-50/70 border border-blue-200 text-xs text-blue-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div className="min-w-0 space-y-0.5">
                <div>
                  Operatori: <strong>{myTeamName}</strong>
                  {stringaSelezionata ? <span> + <strong>{stringaSelezionata}</strong></span> : ' (Solo)'}
                </div>
                <div className="text-[11px] text-blue-800">
                  Mezzo: <strong>{targaInput || 'Non specificato'}</strong> · Guida: <strong>{effectiveAutista}</strong>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Modale */}
        <div className="p-4 sm:p-5 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50 shrink-0">
          {motivo === 'modifica_manuale' ? (
            <button
              type="button"
              disabled={isPending}
              onClick={onClose}
              className="btn-outline px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-100 border-slate-200 cursor-pointer"
            >
              Annulla
            </button>
          ) : (
            <button
              type="button"
              disabled={isPending}
              onClick={onClose}
              className="btn-outline px-3.5 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-100 border-transparent cursor-pointer"
            >
              Salta per ora
            </button>
          )}

          <button
            type="button"
            disabled={isPending}
            onClick={handleSalvaPersonalizzato}
            className="btn-primary px-5 py-2.5 text-xs font-extrabold bg-blue-600 hover:bg-blue-700 text-white shadow-md gap-1.5 cursor-pointer"
          >
            {isPending ? 'Salvataggio...' : 'Conferma Dotazione & Procedi'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
